// The three-shape net-worth rollup invariant (execution plan §6.3):
// `complete` only when every account reports a real balance, `partial` when
// some do and some do not (the partial sum is still shown, with its
// degradation visible), `unavailable` when nothing is usable. Never sums a
// `null` balance as `0`, and `balance` is signed at the source so the
// assets/debts split is just a sign test, never a `kind` negation.
import type { Account } from '@/api/hooks/useAccounts';

export type NetWorthShape = 'complete' | 'partial' | 'unavailable';

export interface NetWorthRollup {
  shape: NetWorthShape;
  /** Sum of every account with a known balance; `null` only when NO account has one. */
  total: number | null;
  /** `null` only when there is no known-balance account with a positive/negative sign respectively -- never a fabricated `0`. */
  assetsTotal: number | null;
  debtsTotal: number | null;
  /** Accounts whose balance is derived/stale (known, but degraded). */
  staleCount: number;
  /** Accounts that have never reported a balance at all (`balance === null`). */
  neverReportedCount: number;
  /** Earliest `as_of` among all non-`ok` accounts; `null` if none is known -- never fabricated. */
  oldestAsOf: string | null;
}

export function computeNetWorthRollup(accounts: Account[]): NetWorthRollup {
  const known = accounts.filter((a) => a.balance !== null);
  const staleCount = accounts.filter(
    (a) => a.balance !== null && (a.balance_status === 'stale' || a.balance_status === 'derived'),
  ).length;
  const neverReportedCount = accounts.filter((a) => a.balance === null).length;

  const allOk = accounts.length > 0 && accounts.every((a) => a.balance_status === 'ok');
  const shape: NetWorthShape = allOk ? 'complete' : known.length === 0 ? 'unavailable' : 'partial';

  let assetsSum = 0;
  let debtsSum = 0;
  let hasAsset = false;
  let hasDebt = false;
  for (const account of known) {
    const value = Number(account.balance);
    if (value >= 0) {
      assetsSum += value;
      hasAsset = true;
    } else {
      debtsSum += value;
      hasDebt = true;
    }
  }
  const assetsTotal = hasAsset ? assetsSum : null;
  const debtsTotal = hasDebt ? debtsSum : null;
  const total = known.length > 0 ? assetsSum + debtsSum : null;

  const degradedAsOfs = accounts
    .filter((a) => a.balance_status !== 'ok' && a.as_of !== null)
    .map((a) => a.as_of as string)
    .sort();
  const oldestAsOf = degradedAsOfs.length > 0 ? degradedAsOfs[0] : null;

  return { shape, total, assetsTotal, debtsTotal, staleCount, neverReportedCount, oldestAsOf };
}
